import { QDataTable, QIconButton, QButton, QDropdownWrapper } from '@kw/quokka-ui';
import { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';

import { DeleteOutline } from '@mui/icons-material';
import { useFetchService } from '../../utils/fetchService';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { OrganizationDropdown } from './OrganizationDropdown';
import { sendHttpRequest } from '../../utils/network.service';
import { formatDateTime } from './utils';

const FAR_FUTURE_ISO = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1000);
  return d.toISOString();
})();

export const Licenses = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('');
  const [selectedSku, setSelectedSku] = useState<string>('');

  // Avoid // in path when org not chosen yet
  const licenseListPath = selectedOrganization ? `/organizations/${selectedOrganization}/license/list` : undefined;

  const { data: licenses = [], mutate } = useFetchService(licenseListPath ?? '', accessToken);

  const { data: skuList = [] } = useFetchService(`/organizations/sku/list`, accessToken);

  const skuOptions = useMemo(() => skuList.map(s => ({ label: s.name, value: s.id })), [skuList]);

  const canGrant = Boolean(selectedOrganization && selectedSku);

  const grantLicense = useCallback(async () => {
    if (canGrant) {
      await sendHttpRequest({
        path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/license`,
        method: 'POST',
        body: {
          skuId: selectedSku,
          organizationId: selectedOrganization,
          startAt: new Date().toISOString(),
          endAt: FAR_FUTURE_ISO,
        },
        successMessage: 'License created successfully',
        errorMessage: 'Error creating license',
        enqueueSnackbar,
      });
    }
    mutate();
  }, [canGrant, enqueueSnackbar, mutate, selectedOrganization, selectedSku]);

  const removeLicense = useCallback(
    async (licenseId: string) => {
      await sendHttpRequest({
        path: `${process.env.ORGANIZATION_SERVICE_URL}/organizations/license/${licenseId}`,
        method: 'DELETE',
        successMessage: 'License deleted successfully',
        errorMessage: 'Error deleting license',
        enqueueSnackbar,
      });
      mutate();
    },
    [enqueueSnackbar, mutate],
  );

  const columns = useMemo(
    () => [
      { field: 'id', headerName: 'License ID' },
      { field: 'skuId', headerName: 'SKU ID' },
      { field: 'organizationId', headerName: 'Organization ID' },
      { field: 'startAt', headerName: 'Start At', renderCell: row => formatDateTime(row.startAt) },
      { field: 'endAt', headerName: 'End At', renderCell: row => formatDateTime(row.endAt) },
      { field: 'deviceLimit', headerName: 'Device Limit' },
      { field: 'days', headerName: 'Days' },
      { field: 'name', headerName: 'Name' },
      {
        field: 'settings',
        headerName: 'Settings',
        renderCell: row => (Array.isArray(row.settings) ? row.settings.join(', ') : ''),
      },
      {
        field: 'skus',
        headerName: 'SKUs',
        renderCell: row => (
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(row.skus ?? [], null, 2)}</pre>
        ),
      },
      { field: 'createdAt', headerName: 'Created At', renderCell: row => formatDateTime(row.createdAt) },
      { field: 'updatedAt', headerName: 'Updated At', renderCell: row => formatDateTime(row.updatedAt) },
      {
        field: 'licenseId',
        headerName: 'Actions',
        renderCell: row => (
          <QIconButton aria-label={`Delete license ${row.id}`} onClick={() => removeLicense(row.id)}>
            <DeleteOutline />
          </QIconButton>
        ),
        width: 100,
      },
    ],
    [removeLicense],
  );

  return (
    <>
      <OrganizationDropdown setSelectedOrganization={setSelectedOrganization} />

      <Box p={2}>
        <QDropdownWrapper
          selectedOption={selectedSku}
          closedLabel={skuOptions.find(o => o.value === selectedSku)?.label ?? 'Select SKU'}
          formControlProps={{ sx: { width: '100%' } }}
          onChange={e => setSelectedSku(e.target.value as string)}
          options={skuOptions}
        />

        <QButton sx={{ mt: 2 }} disabled={!canGrant} variant="filled" onClick={grantLicense}>
          Grant License
        </QButton>
      </Box>

      <QDataTable emptyTableText="No Licenses Found" columns={columns} data={licenses} searchConfig={['id', 'name', 'supportEmail']} />
    </>
  );
};
